/**
 * @license Copyright (c) 2023-2025, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md.
 */

import { createContext } from 'react';
import type { EditorType } from '@ckeditor/ckeditor5-metadata/src';
import { defaultLanguage, type EditorLanguage } from '../components/LanguageSelection/languages';
import type { EditorPlugins } from '../types/EditorPlugins';
import { capabilities, type Capability } from '../constants/capabilities';
import type { PresetView } from '../utils/getPresets/getPresets';
import type { ToolbarOptions } from '../types/ToolbarsOptions';
import { defaultToolbarOptions } from '../constants/toolbars';

export type EditorSettingsContextType = {
	editorType: EditorType;
	onEditorTypeChange: ( newEditorType: EditorType ) => void;
	editorLanguage: EditorLanguage;
	onEditorLanguageChange: ( language: EditorLanguage ) => void;
	editorPlugins: EditorPlugins;
	onEditorPluginsDeselect: ( newPlugins: EditorPlugins ) => void;
	onEditorPluginsSelect: ( newPlugins: EditorPlugins ) => void;
	capabilities: ReadonlyArray<Capability>;
	onCapabilitiesChange: ( newCapability: ReadonlyArray<Capability> ) => void;
	preset: PresetView | null;
	onPresetChange: ( newPreset: PresetView ) => void;
	toolbarOptions: ToolbarOptions;
	onToolbarOptionsChange: ( newToolbarOptions: Partial<ToolbarOptions> ) => void;
	isSelectedPresetModified: boolean;
	builderUrl: string;
};

export const EditorSettingsContext = createContext<EditorSettingsContextType>( {
	editorType: 'ClassicEditor',
	onEditorTypeChange: () => {},
	editorLanguage: defaultLanguage,
	onEditorLanguageChange: () => {},
	editorPlugins: [],
	onEditorPluginsSelect: () => {},
	onEditorPluginsDeselect: () => {},
	capabilities,
	onCapabilitiesChange: () => {},
	preset: null,
	onPresetChange: () => {},
	toolbarOptions: defaultToolbarOptions,
	onToolbarOptionsChange: () => {},
	isSelectedPresetModified: false,
	builderUrl: ''
} );
